import { useApiClient, useFetch } from '@marageti/z4-lib';
import { useState, useCallback } from 'react';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { ReviewRequest, Review, TripMatch } from '@marageti/z4-sdk/lib/travel';
import { Optional } from '@marageti/z4-sdk/lib/utils/type-utils';

const isSubset = (subset: any, superset: any) => {
  if (!subset) return true; // An empty/undefined subset is always a subset of any superset
  if (!superset) return false; // If superset is undefined/null, subset can't be a subset of it
  return Object.entries(subset).every(([key, value]) => Object.prototype.hasOwnProperty.call(superset, key) && superset[key] === value);
};

const useTripMatchReview = ({
  reviewId,
  tripMatchId,
}: {
  reviewId: string | undefined,
  tripMatchId: string | undefined
}) => {
  const apiClient = useApiClient();
  const [submitError, setSubmitError] = useState<ApiError | null>(null);
  const { data: existingReview, isLoading: isLoadingReview, mutate } = useFetch<Review>(apiClient.reviewsClient.getReview, [reviewId], !!reviewId);
  const { data: tripMatch, isLoading: isLoadingTM } = useFetch<TripMatch>(apiClient.tripMatchesClient.getTripMatch, [tripMatchId], true);

  const putReview = useCallback(
    async (reviewRequest: Optional<ReviewRequest, 'travelerId'>, { onSuccess }: { onSuccess?: (data?: any) => any | undefined }) => {
      const hasReviewChanges = !isSubset(reviewRequest, existingReview); // We only update the review if there are changes to the review.
      if (hasReviewChanges && existingReview && reviewId && tripMatch) {
        try {
          const reviewUpdate = {
            ...existingReview,
            ...reviewRequest,
            travelerId: existingReview.travelerId,
            partnerName: tripMatch.partnerName,
          };

          const response = await apiClient.reviewsClient.putReview(reviewId, reviewUpdate);
          mutate(response);
          if (onSuccess) {
            onSuccess(response);
          }
        } catch (error) {
          const apiError = error as ApiError;
          setSubmitError(apiError);
        }
      } else if (onSuccess) {
        onSuccess();
      }
    },
    [apiClient.reviewsClient, existingReview, reviewId, tripMatch],
  );

  const postReview = useCallback(
    async (reviewRequest: Optional<ReviewRequest, 'travelerId'>, { onSuccess }: { onSuccess?: (data: any) => any | undefined }) => {
      if (tripMatchId && tripMatch) {
        try {
          const reviewRequestWithTravelerId = {
            ...reviewRequest,
            travelerId: 'travelerId', // travelerId is not used/gets replaced in the backend if the sender is a traveler, but it is required in the request.
            partnerName: tripMatch.partnerName,
          };
          const createdReview = await apiClient.tripMatchesClient.postTripMatchReview(tripMatchId, reviewRequestWithTravelerId);
          if (onSuccess) {
            onSuccess(createdReview);
          }
        } catch (error) {
          const apiError = error as ApiError;
          setSubmitError(apiError);
        }
      }
    },
    [apiClient.tripMatchesClient, tripMatch, tripMatchId],
  );

  return {
    isLoading: isLoadingReview || isLoadingTM,
    submitError,
    setSubmitError,
    submit: postReview,
    update: putReview,
    existingReview,
    tripMatch,
  };
};

export default useTripMatchReview;
